import { faEyeSlash } from "@fortawesome/free-regular-svg-icons/faEyeSlash";
import { faEye } from "@fortawesome/free-solid-svg-icons/faEye";
import merge from "lodash/merge";
import React, { Fragment } from "react";
import styled from "styled-components";

import { useTheme } from "../../ThemeContext";
import { InputTypes } from "../../model/form/types/InputTypes.enum";
import { Rounded } from "../../theme/Rounded";
import { Spacing } from "../../theme/Spacing";
import { AtiraIcon } from "../AtiraIcon";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { InputProps } from "./InputProps";
import { PasswordInputTypes } from "./InputType.enum";

const ShowHidePasswordButton = styled(Button)`
  position: absolute;
  top: 0.75rem;
  margin: 0;
  padding: 0;
  color: ${(props) => props.theme.main};
  html[dir="rtl"] & {
    left: ${Spacing.m};
  }
  html[dir="ltr"] & {
    right: ${Spacing.m};
  }
`;

// @ts-ignore
const StyledInput = styled.input.attrs((props: InputProps) => ({
  "data-testid": props.dataTestId,
}))<InputProps>`
  width: 100%;
  transition: box-shadow 0.2s;
  outline: none !important;
  border-radius: ${Rounded.md};
  height: 2.3rem;
  font-family: inherit;
  font-size: ${Spacing.m};
  border: none;

  &:disabled {
    background-color: ${({ theme }) => theme.darkerSub} !important;
    cursor: not-allowed;
  }

  html[dir="rtl"] &::placeholder {
    text-align: right !important;
  }

  ${({ originalType }) =>
    originalType === "password" &&
    ` html[dir="rtl"] & {
        padding-left: ${Spacing.xxl} !important;
      }

      html[dir="ltr"] & {
        padding-right: ${Spacing.xxl} !important;
      }
  `}
`;

const StyledInputWrapper = styled(Flex)`
  gap: 0.2rem;
  align-items: center;
  border-radius: ${Rounded.md};
`;

type RegularInputProps = InputProps & {
  inputType?: React.HTMLInputTypeAttribute;
  togglePasswordInputType: () => void;
};

export const RegularInputComponent: React.FC<RegularInputProps> = ({
  inputType,
  errorMessage,
  togglePasswordInputType,
  type,
  ref,
  ...props
}) => {
  const { theme } = useTheme();

  const isNumeric = type === InputTypes.NUMBER?.toLowerCase();
  const effectiveType: React.HTMLInputTypeAttribute = isNumeric
    ? "text"
    : inputType || "text";

  const sanitizeNumeric = (raw: string) => {
    const v = String(raw ?? "");
    const digits = v.replace(/\D+/g, "");
    if (digits.length > 1) {
      // if only zeros, compress to single zero; else drop leading zeros
      return /^0+$/.test(digits) ? "0" : digits.replace(/^0+/, "");
    }
    return digits;
  };

  const handleNumericChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = sanitizeNumeric(e.target.value) as any;
    props.onChange?.({
      ...e,
      target: { ...e.target, value: newValue },
      currentTarget: { ...e.currentTarget, value: newValue },
    } as any);
  };

  const handlePaste = isNumeric
    ? (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const text = sanitizeNumeric(e.clipboardData.getData("text")).slice(
          0,
          props.maxLength,
        );
        props.onChange?.(text as any);
      }
    : undefined;

  const getContent = (children: React.ReactNode) => {
    if (props.icon) {
      return (
        <StyledInputWrapper
          opacity={props.disabled ? "0.2" : "1"}
          border={
            props.valid === false
              ? `2px solid ${theme.danger}`
              : `1px solid ${theme.lightgray}`
          }
          borderRadius={props.borderRadius || "md"}
          style={{ padding: `0 ${Spacing.s}` }}
        >
          {children}
          <AtiraIcon icon={props.icon} size={props.iconSize || "lg"} />
        </StyledInputWrapper>
      );
    }

    return (
      <Fragment>
        {children}
        {type === PasswordInputTypes.PASSWORD.toLowerCase() ? (
          <ShowHidePasswordButton
            onClick={togglePasswordInputType}
            icon={
              inputType === PasswordInputTypes.PASSWORD.toLowerCase()
                ? faEyeSlash
                : faEye
            }
            backgroundColor="transparent"
            hover={false}
          />
        ) : null}
      </Fragment>
    );
  };

  const baseStyle = props.icon
    ? merge(props.style, {
        height: props.height,
        borderRadius: props.borderRadius,
        placeholder: props.placeholder,
        margin: props.margin,
      })
    : merge(
        props.style,
        props.disabled ? { opacity: "0.2" } : {},
        props.valid === false
          ? { border: `2px solid ${theme.danger}`, boxShadow: "unset" }
          : { border: props.border || `1px solid ${theme.lightgray}` },
        {
          width: "100%",
          height: props.height,
          borderRadius: props.borderRadius,
          placeholder: props.placeholder,
          padding: props.padding || `${Spacing.none} ${Spacing.s}`,
          margin: props.margin,
        },
      );

  return (
    <Flex flexDirection="column" position="relative" gap="s" flex={2}>
      {getContent(
        <StyledInput
          ref={ref}
          style={baseStyle}
          {...props}
          dir="auto"
          type={effectiveType}
          value={
            isNumeric
              ? (sanitizeNumeric(props.value as string) as any)
              : (props as any).value
          }
          inputMode={isNumeric ? "numeric" : undefined}
          onPaste={handlePaste}
          onChange={isNumeric ? handleNumericChange : props.onChange}
          originalType={type?.toLowerCase()}
        />,
      )}
    </Flex>
  );
};
